import { Moment } from 'moment'

import { ConfigItem, DefaultFile, SearchResult } from 'Core/interfaces'
import { ConfigFilterItem } from 'Core/interfaces/filters'

import { BillingLocationStatus } from 'Portals/Internal/constants'

import { Contact } from './Contact'
import { AddAddressFormDataLabels } from './Miscellaneous'

export enum BillingLocationLabels {
	CustomerId = 'customerId',
	AddressSequenceNumber = 'addressSequenceNumber',
	CustomerName = 'customerName',
	CompanyId = 'companyId',
	BillingAgencyId = 'billingAgencyId',
	BillingCurrencyId = 'billingCurrencyId',
	CustomerStatusId = 'customerStatusId',
	ManualInvoiceReview = 'manualInvoiceReview',
	AllowSelectionOfCustomerReviewInvoiceStatus = 'allowSelectionOfCustomerReviewInvoiceStatus',
	EmailInvoiceToServiceSiteContacts = 'emailInvoiceToServiceSiteContacts',
	PurchaseOrderRequired = 'purchaseOrderRequired',
	CreditCardRequired = 'creditCardRequired',
	HesSharedCustomer = 'hesSharedCustomer',
	SendStatement = 'sendStatement',
	RebateChecksIssued = 'rebateChecksIssued',
	SupplierLocationId = 'supplierLocationId',
	CustomerReferenceCode = 'customerReferenceCode',
	StatementComments = 'statementComments',
	EmailInvoiceComments = 'emailInvoiceComments',
	TaxCorporationCode = 'taxCorporationCode',
	LateFeesPercent = 'lateFeesPercent',
	Company = 'company',
	Id = 'id',
	PeopleSoftCustomer = 'peopleSoftCustomer',
	CountryTaxNumber = 'countryTaxNumber',
	EfacturaFormOfPayment = 'efacturaFormOfPayment',
	EfacturaCTA = 'efacturaCTA',
	EfacturaUseCode = 'efacturaUseCode',
	EfacturaPaymentMethod = 'efacturaPaymentMethod',
	RegimenFiscal = 'regimenFiscal',
	SupplierLocation = 'supplierLocation',
	CustomerUpdated = 'customerUpdated',
	LastInvoiceDate = 'lastInvoiceDate',
	CustomerStatus = 'customerStatus',
	BillingAgency = 'billingAgency',
	BillingCurrency = 'billingCurrency',
	BillingCurrencyCode = 'billingCurrencyCode',
	VatOffice = 'vatOffice',
	CustomerCompany = 'customerCompany',
	CustomerCompanyId = 'customerCompanyId',
	GroupCode = 'groupCode',
	CustomerGroupCode = 'customerGroupCode',
	AllowSelectionOfCustomerReviewInvoiceStatusCode = 'allowSelectionOfCustomerReviewInvoiceStatusCode'
}

export enum BillingLocationAddressSearchSuggestions {
	stateResponse = 'stateResponse',
	cityResponse = 'cityResponse',
	addressResponse = 'addressResponse'
}

export enum BillingLocationSearchSuggFields {
	searchByCustomerId = 'idResponse',
	searchByCustomerName = 'customerNameResponse',
	searchByCompanyName = 'companyName',
	searchByPoNumber = 'poNumber'
}

export enum BillingLocationFilterFields {
	notMappedToPS = 'notMappedToPS'
}

export enum PurchaseOrderFields {
	serviceSitesIds = 'serviceSitesIds',
	includeAllServiceSites = 'includeAllServiceSites',
	billingFrequencyIds = 'billingFrequencyIds',
	purchaseOrderNumber = 'purchaseOrderNumber',
	amount = 'amount',
	effectiveStartDate = 'effectiveStartDate',
	effectiveEndDate = 'effectiveEndDate',
	document = 'document',
	documentName = 'documentName',
	CXmlFileName = 'CXmlFileName',
	comments = 'comments'
}

export enum PurchOrderServSiteSugg {
	searchResultById = 'searchResultById',
	searchResultByName = 'searchResultByName',
	searchResultByCompanyName = 'searchResultByCompanyName'
}

export enum PurchOrderBillingFreqSugg {
	searchByCode = 'searchByCode',
	searchByCustomerId = 'searchByCustomerId',
	searchByCustomerName = 'searchByCustomerName',
	searchByServiceSiteId = 'searchByServiceSiteId',
	searchByCompanyId = 'searchByCompanyId',
	searchByBillingAgency = 'searchByBillingAgency'
}

export enum PurchaseOrderHistoryFields {
	customerInvoiceId = 'customerInvoiceId',
	totalInvoiceAmount = 'totalInvoiceAmount',
	totalModifiedExtendedPrice = 'totalModifiedExtendedPrice',
	balanceLessTotalExtendedPrice = 'balanceLessTotalExtendedPrice',
	balanceLessModifiedExtendedPrice = 'balanceLessModifiedExtendedPrice',
	invoiceCreateDate = 'invoiceCreateDate',
	currency = 'currency'
}

export enum PurchaseOrderXMLDataFields {
	id = 'id',
	purchaseOrderNumber = 'purchaseOrderNumber',
	amount = 'amount',
	fileName = 'fileName'
}

export declare namespace BillingLocation {
	interface Basic extends Entity {
		companyName: string
		address: string
		status: BillingLocationStatus
		location: string
		updated: string
		billingAddress: {
			city: string
			state: string
			stateProvince: Entity
		}
	}

	interface Detailed
		extends Omit<
			Full,
			| BillingLocationLabels.CustomerName
			| BillingLocationLabels.CompanyId
			| BillingLocationLabels.Company
			| BillingLocationLabels.AllowSelectionOfCustomerReviewInvoiceStatusCode
		> {
		name: string
		[BillingLocationLabels.Company]: Entity | null
		[BillingLocationLabels.AllowSelectionOfCustomerReviewInvoiceStatus]: NameCode | null
		isTurkish: boolean
	}

	interface Full extends Form.Add {
		[BillingLocationLabels.Id]: number
		[BillingLocationLabels.PeopleSoftCustomer]: PeopleSoftCustomer | null
		[BillingLocationLabels.CustomerReferenceCode]: string
		[BillingLocationLabels.StatementComments]: string
		[BillingLocationLabels.EmailInvoiceComments]: string | null
		[BillingLocationLabels.TaxCorporationCode]: string | null
		[BillingLocationLabels.Company]: string
		[BillingLocationLabels.EfacturaPaymentMethod]: string | null
		[BillingLocationLabels.CountryTaxNumber]: string
		[BillingLocationLabels.EfacturaFormOfPayment]: string | null
		[BillingLocationLabels.EfacturaCTA]: string
		[BillingLocationLabels.LateFeesPercent]: string
		[BillingLocationLabels.EfacturaUseCode]: string | null
		[BillingLocationLabels.EfacturaPaymentMethod]: string | null
		[BillingLocationLabels.RegimenFiscal]: string | null
		[BillingLocationLabels.SupplierLocation]: string
		[BillingLocationLabels.CustomerUpdated]: string
		[BillingLocationLabels.LastInvoiceDate]: string | null
		[BillingLocationLabels.CustomerStatus]: string
		[BillingLocationLabels.BillingAgency]: string
		[BillingLocationLabels.BillingCurrency]: string
		[BillingLocationLabels.BillingCurrencyCode]: string
		[BillingLocationLabels.VatOffice]: string | null
		[BillingLocationLabels.GroupCode]: NameCode | null
		[BillingLocationLabels.CustomerCompany]: Entity | null
		[BillingLocationLabels.CustomerGroupCode]: Key | null
		[BillingLocationLabels.CustomerCompanyId]: Key | null
	}

	interface PeopleSoftCustomer extends BillingLocation.Address.Billing.Full {
		addressSequenceNumber: number
		customerId: string
		stateId: number
	}

	namespace Form {
		interface Add {
			[BillingLocationLabels.CustomerId]: string
			[BillingLocationLabels.AddressSequenceNumber]: Key
			[BillingLocationLabels.CustomerName]: string
			[BillingLocationLabels.CompanyId]: LabelValue<number> | null | number
			[BillingLocationLabels.BillingAgencyId]: number | null
			[BillingLocationLabels.BillingCurrencyId]: number | null
			[BillingLocationLabels.CustomerStatusId]: number | null
			[BillingLocationLabels.SupplierLocationId]: LabelValue<number> | null | Key
			[BillingLocationLabels.ManualInvoiceReview]: boolean
			[BillingLocationLabels.AllowSelectionOfCustomerReviewInvoiceStatusCode]: string | null
			[BillingLocationLabels.EmailInvoiceToServiceSiteContacts]: boolean
			[BillingLocationLabels.PurchaseOrderRequired]: boolean
			[BillingLocationLabels.CreditCardRequired]: boolean
			[BillingLocationLabels.HesSharedCustomer]: boolean
			[BillingLocationLabels.SendStatement]: boolean
			[BillingLocationLabels.RebateChecksIssued]: boolean
		}

		interface Edit extends Add {
			[BillingLocationLabels.Id]: number
			[BillingLocationLabels.PeopleSoftCustomer]: PeopleSoftCustomer | null
			[BillingLocationLabels.CustomerReferenceCode]: string
			[BillingLocationLabels.StatementComments]: string
			[BillingLocationLabels.CountryTaxNumber]: string
			[BillingLocationLabels.EfacturaPaymentMethod]: string | null
			[BillingLocationLabels.EfacturaCTA]: string
			[BillingLocationLabels.EmailInvoiceComments]: string | null
			[BillingLocationLabels.TaxCorporationCode]: string | null
			[BillingLocationLabels.EfacturaFormOfPayment]: string | null
			[BillingLocationLabels.LateFeesPercent]: string
			[BillingLocationLabels.EfacturaUseCode]: string | null
			[BillingLocationLabels.EfacturaPaymentMethod]: string | null
			[BillingLocationLabels.RegimenFiscal]: string | null
			[BillingLocationLabels.VatOffice]: string | null
			[BillingLocationLabels.CustomerGroupCode]: Key | null
			[BillingLocationLabels.CustomerCompanyId]: Key | null
		}
	}

	interface SearchByCustomer extends Entity {
		customerName: string
	}

	interface SearchByServiceSite extends Entity {
		serviceSiteName: string
	}

	namespace Search {
		interface Suggestions {
			[BillingLocationSearchSuggFields.searchByCustomerId]: Entity[]
			[BillingLocationSearchSuggFields.searchByCustomerName]: Entity[]
			[BillingLocationSearchSuggFields.searchByCompanyName]: Entity['name'][]
		}

		interface Result extends SearchResult {}

		interface BillingFrequenciesResult {
			searchByCode: Entity[]
			searchByCustomer: SearchByCustomer[]
			searchByServiceSite: SearchByServiceSite[]
		}

		interface Config {
			[BillingLocationSearchSuggFields.searchByCustomerId]: ConfigItem<BillingLocationSearchSuggFields>
			[BillingLocationSearchSuggFields.searchByCustomerName]: ConfigItem<BillingLocationSearchSuggFields>
			[BillingLocationSearchSuggFields.searchByCompanyName]: ConfigItem<BillingLocationSearchSuggFields>
			[BillingLocationSearchSuggFields.searchByPoNumber]: ConfigItem<BillingLocationSearchSuggFields>
		}
	}

	namespace Filter {
		interface Config {
			[BillingLocationFilterFields.notMappedToPS]: ConfigFilterItem<string, boolean>
		}
	}

	namespace Address {
		interface Basic {
			streetAddress: string
			city: BasicString
			state: BasicString
			zip: BasicString
		}

		interface Full {
			id: Key
			addressTypeId: Key
			addressType: string
			contact: string
			contactId: Key
			addressLine1: string
			addressLine2: string
			city: string
			state: string
			stateProvinceId: Key
			zipCode: string
		}

		namespace Billing {
			interface Full {
				companyName: string
				addressLine1: string
				addressLine2: string
				addressActive: boolean
				customerActive: boolean
				city: string
				stateCode: string
				zipCode: string
				customerId: string
				addressSequenceNumber: Key
			}
		}

		namespace Form {
			interface Generic {
				[AddAddressFormDataLabels.AddressType]: Key | null
				[AddAddressFormDataLabels.Contact]: Key | null
				[AddAddressFormDataLabels.AddressLine1]: string
				[AddAddressFormDataLabels.AddressLine2]: string
				[AddAddressFormDataLabels.City]: string
				[AddAddressFormDataLabels.StateProvince]: LabelValue | null
				[AddAddressFormDataLabels.ZipCode]: string
			}
		}

		namespace Request {
			interface Generic
				extends Omit<
					BillingLocation.Address.Full,
					'id' | 'addressType' | 'contact' | 'state'
				> {}
		}

		namespace Search {
			interface Suggestions {
				[BillingLocationAddressSearchSuggestions.stateResponse]: Entity[]
				[BillingLocationAddressSearchSuggestions.cityResponse]: Entity['name'][]
				[BillingLocationAddressSearchSuggestions.addressResponse]: Entity['name'][]
			}

			interface Config {
				[BillingLocationAddressSearchSuggestions.stateResponse]: ConfigItem<BillingLocationAddressSearchSuggestions>
				[BillingLocationAddressSearchSuggestions.cityResponse]: ConfigItem<BillingLocationAddressSearchSuggestions>
				[BillingLocationAddressSearchSuggestions.addressResponse]: ConfigItem<BillingLocationAddressSearchSuggestions>
			}
		}
	}

	namespace ServiceLocation {
		interface Basic extends Entity {
			serviceLocationAddress: Address.Basic | null
		}
	}
	namespace PurchaseOrder {
		interface Basic {
			id: number
			purchaseOrderNumber: string
			amountLeft: number
			lessTotalInvoiceAmount: number
			lessModifiedExtendedPrice: number
			effectiveStartDate: BasicString
			effectiveEndDate: BasicString
			serviceSite: Entity
			customBillingFrequencyCode: string
			rowId: number
			comments: BasicString
			status: string
		}

		interface Detailed extends Omit<Basic, 'serviceSite' | 'customBillingFrequencyCode'> {
			serviceSites: Entity[]
			billingFrequencies: Entity[]
			documentName: BasicString
			amount: number
		}

		interface History {
			[PurchaseOrderHistoryFields.customerInvoiceId]: number
			[PurchaseOrderHistoryFields.totalInvoiceAmount]: number
			[PurchaseOrderHistoryFields.totalModifiedExtendedPrice]: number
			[PurchaseOrderHistoryFields.balanceLessTotalExtendedPrice]: number
			[PurchaseOrderHistoryFields.balanceLessModifiedExtendedPrice]: number
			[PurchaseOrderHistoryFields.invoiceCreateDate]: BasicString
			[PurchaseOrderHistoryFields.currency]: BasicString
		}

		interface XMLData {
			[PurchaseOrderXMLDataFields.id]: number
			[PurchaseOrderXMLDataFields.purchaseOrderNumber]: BasicString
			[PurchaseOrderXMLDataFields.amount]: number | null
			[PurchaseOrderXMLDataFields.fileName]: BasicString
		}

		namespace Form {
			interface Generic {
				[PurchaseOrderFields.serviceSitesIds]: LabelValue[]
				[PurchaseOrderFields.includeAllServiceSites]: boolean
				[PurchaseOrderFields.billingFrequencyIds]: LabelValue[]
				[PurchaseOrderFields.purchaseOrderNumber]: BasicString
				[PurchaseOrderFields.amount]: BasicString
				[PurchaseOrderFields.effectiveStartDate]: Moment | null
				[PurchaseOrderFields.effectiveEndDate]: Moment | null
				[PurchaseOrderFields.document]: DefaultFile | null
				[PurchaseOrderFields.documentName]?: BasicString
				[PurchaseOrderFields.CXmlFileName]: BasicString
				[PurchaseOrderFields.comments]: BasicString
			}
		}
		namespace Request {
			interface Generic {
				[PurchaseOrderFields.serviceSitesIds]: Key[]
				[PurchaseOrderFields.billingFrequencyIds]: Key[]
				[PurchaseOrderFields.purchaseOrderNumber]: string
				[PurchaseOrderFields.amount]: string
				[PurchaseOrderFields.effectiveStartDate]: string
				[PurchaseOrderFields.effectiveEndDate]: BasicString
				isServiceSiteReference: boolean
				'File.Value': File | null
				'File.HasValue': boolean
				[PurchaseOrderFields.CXmlFileName]: BasicString
				[PurchaseOrderFields.comments]: BasicString
			}
		}
		namespace Search {
			interface CompanyName extends Entity {
				companyName: string
			}

			interface CompanyNameCode extends CodeEntity {
				companyName: string
			}

			interface CustomerId extends CompanyNameCode {
				customerId: number
			}

			interface ServiceSiteId extends CompanyNameCode {
				serviceSiteId: number
			}

			interface CompanyId extends CompanyNameCode {
				companyId: number
			}

			interface BillingAgency extends CompanyNameCode {
				billingAgencyName: string
			}

			namespace ServiceSite {
				interface Result {
					[PurchOrderServSiteSugg.searchResultById]: Entity[]
					[PurchOrderServSiteSugg.searchResultByName]: Entity[]
					[PurchOrderServSiteSugg.searchResultByCompanyName]: CompanyName[]
				}
			}
		}

		namespace ApproveReject {
			interface Form {
				isRequired: boolean
				[PurchaseOrderFields.serviceSitesIds]: BasicString
				[PurchaseOrderFields.includeAllServiceSites]: boolean
				[PurchaseOrderFields.billingFrequencyIds]: LabelValue[]
				[PurchaseOrderFields.purchaseOrderNumber]: BasicString
				[PurchaseOrderFields.amount]: BasicString
				[PurchaseOrderFields.effectiveStartDate]: Moment | null
				[PurchaseOrderFields.effectiveEndDate]: Moment | null
				[PurchaseOrderFields.document]: DefaultFile | null
				[PurchaseOrderFields.documentName]?: BasicString
				[PurchaseOrderFields.CXmlFileName]: BasicString
				[PurchaseOrderFields.comments]: BasicString
			}

			interface Request {
				commets: BasicString
			}
		}
	}
}

export interface AddBlContactData extends BlContactFlags {
	contactId: string
	isActive: boolean
}

export interface BlContactFlags {
	statementContact: boolean
	discrepancyContact: boolean
	invoiceContact: boolean
}

export interface AddContactFormData extends Contact.Form.Create.Basic, BlContactFlags {}

export interface CreateBlContact extends Contact.Form.Create.Request, BlContactFlags {}

export interface BlContactData extends Contact.Search.Result, AddBlContactData {}
